/**
 * PRD.330 Phase 5 — Time-series column visualization (no chart dependency).
 *
 * An ordered list of calendar buckets: each item's period label, amount and
 * count are TEXT (the authoritative values, readable with CSS off / by
 * assistive tech); the column bar is decorative (aria-hidden, not focusable)
 * and sized from RAW numbers via `barFraction`. Data comes only from the
 * visualization's own grouped query — never from report rows. For a
 * single-day range (`bucket === null`) the panel still renders, showing
 * "N/A", so the report layout stays the same across date ranges.
 * Dense ranges scroll horizontally inside the panel only.
 */
import type { KeyboardEvent, ReactNode } from 'react';
import { Button } from '@helios/ui';
import type { ReportTimeSeriesDef, ReportTimeSeriesResult } from '@helios/domain';
import {
  barFraction,
  timeSeriesGroupingLabel,
  timeSeriesPointLabel,
  timeSeriesSpansFullRow,
  timeSeriesSubtitle,
} from './report-view-logic';
import { shouldClearOnEscape } from './category-filter-logic';

const BAR_HEIGHT = 120;
const COLUMN_WIDTH = 84;

export function ReportTimeSeriesBars({
  visualization,
  result,
  formatPrimary,
  formatSecondary = (n) => String(n),
  emptyMessage = 'No data for the selected filters and date range.',
  notApplicableMessage = 'Select a multi-day range to view this over time.',
  color = 'var(--color-primary)',
  onPointActivate,
  drill = null,
  onRetry,
}: {
  visualization: ReportTimeSeriesDef<string>;
  result: ReportTimeSeriesResult | undefined;
  formatPrimary: (value: number) => ReactNode;
  /** e.g. (n) => `${n} payments`. */
  formatSecondary?: (value: number) => ReactNode;
  emptyMessage?: string;
  /** Shown when the applied range is a single day (no time buckets). */
  notApplicableMessage?: string;
  color?: string;
  /**
   * Optional drill-down: when given, each period is a real <button> that
   * narrows the report's date range to it (the caller owns that state).
   */
  onPointActivate?: (point: { from: string; to: string }) => void;
  /** Present while drilled in: what is shown and where Back returns to. */
  drill?: { currentLabel: string; backLabel: string; onBack: () => void } | null;
  onRetry?: () => void;
}) {
  if (!result) return null; // no binding for this visualization
  const { bucket, points, status } = result;
  const interactive = onPointActivate !== undefined;

  // Escape goes back one drill level — scoped to this card (no window/document
  // listener) and ignored if another handler already handled the key.
  const onKeyDown = drill
    ? (e: KeyboardEvent<HTMLElement>) => {
        if (shouldClearOnEscape(e, 1)) {
          e.preventDefault();
          drill.onBack();
        }
      }
    : undefined;

  const backLink = drill && (
    <>
      <span>
        Showing <strong style={{ color: 'var(--text)' }}>{drill.currentLabel}</strong>
      </span>
      <span aria-hidden="true">·</span>
      <button
        type="button"
        onClick={drill.onBack}
        style={{
          padding: 0,
          border: 'none',
          background: 'none',
          font: 'inherit',
          color: 'var(--color-primary)',
          textDecoration: 'underline',
          cursor: 'pointer',
        }}
      >
        ← Back to {drill.backLabel}
      </button>
      <span aria-hidden="true">·</span>
      <span>Esc to go back</span>
    </>
  );

  // Single-day range: a compact one-row panel (title + N/A) — always shown so
  // the report layout stays consistent, without reserving chart height.
  if (status === 'notApplicable' || bucket === null) {
    return (
      <section
        aria-label={visualization.title}
        // Compact status card: takes its own full-width row in the report's
        // visualization area, so the next card gets the full width below it.
        data-viz-layout="full-row"
        onKeyDown={onKeyDown}
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'baseline',
          gap: 'var(--sp-1) var(--sp-3)',
          background: 'var(--bg-surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--sp-2) var(--sp-4)',
          boxSizing: 'border-box',
        }}
      >
        <h2
          style={{
            margin: 0,
            fontSize: 'var(--font-size-base)',
            fontWeight: 'var(--font-weight-semi)',
            color: 'var(--text)',
          }}
        >
          {visualization.title}
        </h2>
        <span
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-semi)',
            color: 'var(--text-muted)',
          }}
        >
          {timeSeriesSubtitle(null)}
        </span>
        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
          {notApplicableMessage}
        </span>
        {backLink && (
          <span
            style={{
              display: 'inline-flex',
              flexWrap: 'wrap',
              gap: '2px 6px',
              fontSize: 'var(--font-size-xs)',
              color: 'var(--text-muted)',
            }}
          >
            {backLink}
          </span>
        )}
      </section>
    );
  }

  const hasSecondary = visualization.secondaryMeasure !== undefined;
  const max = points.reduce((m, p) => Math.max(m, p.primary), 0);
  const empty =
    status === 'ready' && points.every((p) => p.primary === 0 && (p.secondary ?? 0) === 0);
  const withYear =
    points.length > 0 && points[0]!.from.slice(0, 4) !== points[points.length - 1]!.to.slice(0, 4);
  const minHeight = BAR_HEIGHT + 120;

  const message = (text: ReactNode, action?: ReactNode) => (
    <div
      style={{
        minHeight: minHeight - 60,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--sp-3)',
        color: 'var(--text-muted)',
        fontSize: 'var(--font-size-sm)',
        textAlign: 'center',
      }}
    >
      <div>{text}</div>
      {action}
    </div>
  );

  // One period: decorative bar (aria-hidden) + exact text values.
  const columnContent = (p: (typeof points)[number]) => (
    <>
      <div
        aria-hidden="true"
        style={{
          height: BAR_HEIGHT,
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'center',
          background: 'var(--bg-surface-2, #f1f5f9)',
          borderRadius: 'var(--radius-sm, 4px)',
        }}
      >
        <div
          style={{
            width: '60%',
            height: `${barFraction(p.primary, max) * 100}%`,
            minHeight: p.primary > 0 ? 2 : 0,
            background: color,
            borderRadius: 'var(--radius-sm, 4px) var(--radius-sm, 4px) 0 0',
          }}
        />
      </div>
      <span
        style={{
          marginTop: 6,
          fontSize: 'var(--font-size-xs)',
          fontWeight: 'var(--font-weight-medium)',
          color: 'var(--text)',
        }}
      >
        {timeSeriesPointLabel(p, bucket ?? 'day', withYear)}
      </span>
      <span
        style={{
          fontSize: 'var(--font-size-xs)',
          color: 'var(--text)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {formatPrimary(p.primary)}
      </span>
      {hasSecondary && (
        <span
          style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--text-muted)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {formatSecondary(p.secondary ?? 0)}
        </span>
      )}
    </>
  );

  let body: ReactNode;
  if (status === 'error') {
    body = message(
      "Couldn't load this visualization.",
      onRetry && (
        <Button type="button" size="sm" variant="outline" onClick={onRetry}>
          Retry
        </Button>
      ),
    );
  } else if (status === 'loading' && points.length === 0) {
    body = message('Loading…');
  } else if (empty) {
    body = message(emptyMessage);
  } else {
    body = (
      <div style={{ overflowX: 'auto', paddingBottom: 'var(--sp-1)' }}>
        <ol
          aria-label={`${visualization.title}, ${timeSeriesGroupingLabel(bucket).toLowerCase()}`}
          style={{
            display: 'flex',
            gap: 4,
            listStyle: 'none',
            margin: 0,
            padding: 0,
            minWidth: 'max-content',
            opacity: status === 'loading' ? 0.55 : 1,
            transition: 'opacity 120ms ease',
          }}
        >
          {points.map((p) => (
            <li
              key={p.key}
              style={{
                width: COLUMN_WIDTH,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                textAlign: 'center',
              }}
            >
              {interactive ? (
                <button
                  type="button"
                  onClick={() => onPointActivate({ from: p.from, to: p.to })}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    width: '100%',
                    padding: 0,
                    border: 'none',
                    background: 'none',
                    font: 'inherit',
                    color: 'inherit',
                    textAlign: 'center',
                    cursor: 'pointer',
                    borderRadius: 'var(--radius-sm, 4px)',
                  }}
                >
                  {columnContent(p)}
                </button>
              ) : (
                columnContent(p)
              )}
            </li>
          ))}
        </ol>
      </div>
    );
  }

  return (
    <section
      aria-label={visualization.title}
      // Long daily series (e.g. This month / Last month) take a full-width row.
      data-viz-layout={timeSeriesSpansFullRow(result) ? 'full-row' : undefined}
      onKeyDown={onKeyDown}
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--sp-3) var(--sp-4)',
        minHeight,
        minWidth: 0,
        boxSizing: 'border-box',
      }}
    >
      <h2
        style={{
          margin: 0,
          fontSize: 'var(--font-size-base)',
          fontWeight: 'var(--font-weight-semi)',
          color: 'var(--text)',
        }}
      >
        {visualization.title}
      </h2>
      <p
        style={{
          margin: '2px 0 var(--sp-3)',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--text-muted)',
        }}
      >
        {timeSeriesSubtitle(bucket)}
      </p>
      {interactive && (
        <p
          style={{
            margin: '0 0 var(--sp-2)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--text-muted)',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '2px 6px',
          }}
        >
          {backLink || <span>Click a period to drill into it.</span>}
        </p>
      )}
      {body}
    </section>
  );
}
